
import { ShoppingList } from "@/types";
import { networkService } from "@/services/network.service";
import { offlineDb } from "@/db/offlineDb";
import OfflineLogger from "@/utils/offlineLogger";
import { getShoppingLists } from "@/services/shopping/lists.service";
import { primaryListOfflineService } from "@/services/offline/primary-list-offline.service";

/**
 * ✅ FIX: Servizio migliorato per ottenere la lista primaria con protezione anti-race-condition
 */
export const getEnhancedPrimaryList = async (): Promise<ShoppingList | null> => {
  try {
    OfflineLogger.info('getEnhancedPrimaryList', 'Starting enhanced primary list fetch');

    const userId = localStorage.getItem('userId') || 'offline-user';

    if (networkService.isOnline()) {
      try {
        // ✅ Prova prima online
        OfflineLogger.info('getEnhancedPrimaryList', 'Fetching primary list online');
        const onlineLists = await getShoppingLists();
        const primaryList = onlineLists.find(list => list.isPrimary || list.is_primary);
        
        if (primaryList) {
          // ✅ Casha la lista primaria offline per uso futuro
          await cachePrimaryListOffline(primaryList);
          OfflineLogger.info('getEnhancedPrimaryList', `Found and cached primary list: ${primaryList.name}`);
          console.debug('[PrimaryList]', 'source: online', {
            total: primaryList.items?.length ?? 0,
            checked: primaryList.items?.filter(i => i.checked).length ?? 0
          });
          return primaryList;
        } else {
          OfflineLogger.warn('getEnhancedPrimaryList', 'No primary list found online, trying offline');
        }
      } catch (error) {
        OfflineLogger.error('getEnhancedPrimaryList.online', error);
      }
    }

    // ✅ Fallback su cache offline
    OfflineLogger.info('getEnhancedPrimaryList', 'Trying offline cache');
    const offlinePrimaryList = await primaryListOfflineService.getPrimaryListOffline(userId);

    if (offlinePrimaryList) {
      OfflineLogger.info('getEnhancedPrimaryList', `Found offline primary list: ${offlinePrimaryList.name}`);
      console.debug('[PrimaryList]', 'source: offline', {
        total: offlinePrimaryList.items?.length ?? 0,
        checked: offlinePrimaryList.items?.filter(i => i.checked).length ?? 0
      });
      return offlinePrimaryList;
    }

    // ✅ FIX: Usa il servizio protetto per creare la lista primaria
    OfflineLogger.info('getEnhancedPrimaryList', 'No primary list found, creating with protection');
    const primaryListId = await primaryListOfflineService.ensurePrimaryListExists(userId);

    if (primaryListId) {
      const list = await primaryListOfflineService.getPrimaryListOffline(userId);
      console.debug('[PrimaryList]', 'source: offline', {
        total: list?.items?.length ?? 0,
        checked: list?.items?.filter(i => i.checked).length ?? 0
      });
      return list;
    }

    // ✅ Ultimo fallback: crea una lista primaria di emergenza
    const emergencyList = createEmergencyPrimaryList();
    console.debug('[PrimaryList]', 'source: emergency', {
      total: emergencyList.items?.length ?? 0,
      checked: emergencyList.items?.filter(i => i.checked).length ?? 0
    });
    return emergencyList;

  } catch (error) {
    OfflineLogger.error('getEnhancedPrimaryList', error);
    const emergencyList = createEmergencyPrimaryList();
    console.debug('[PrimaryList]', 'source: emergency', {
      total: emergencyList.items?.length ?? 0,
      checked: emergencyList.items?.filter(i => i.checked).length ?? 0
    });
    return emergencyList;
  }
};

/**
 * ✅ Casha la lista primaria nel database locale
 */
async function cachePrimaryListOffline(primaryList: ShoppingList): Promise<void> {
  try {
    // ✅ FIX: Controlla se esiste già prima di sovrascrivere
    const existing = await offlineDb.shoppingLists.get(primaryList.id);
    if (existing) {
      OfflineLogger.info('cachePrimaryListOffline', `Primary list already cached: ${primaryList.name}`);
      return;
    }

    await offlineDb.shoppingLists.put({
      id: primaryList.id,
      name: primaryList.name,
      user_id: primaryList.user_id,
      is_primary: true,
      rapid_insert: primaryList.rapid_insert || false,
      shop_id: primaryList.shop_id || null,
      background_gradient: primaryList.backgroundGradient || primaryList.background_gradient || null,
      created_at: primaryList.createdAt?.toISOString() || new Date().toISOString(),
      updated_at: primaryList.updatedAt?.toISOString() || new Date().toISOString(),
      _syncStatus: 'synced' as const,
      _lastModified: Date.now()
    });
    OfflineLogger.info('cachePrimaryListOffline', `Primary list cached: ${primaryList.name}`);
  } catch (error) {
    OfflineLogger.error('cachePrimaryListOffline', error);
  }
}

/**
 * ✅ Crea una lista primaria di emergenza in memoria
 */
function createEmergencyPrimaryList(): ShoppingList {
  const emergencyList: ShoppingList = {
    id: 'emergency-primary-list',
    name: 'Lista della Spesa (Emergenza)',
    user_id: 'offline-user',
    isPrimary: true,
    is_primary: true,
    rapid_insert: false,
    shop_id: undefined,
    backgroundGradient: undefined,
    background_gradient: undefined,
    createdAt: new Date(),
    updatedAt: new Date(),
    items: [],
    collaborators: []
  };

  OfflineLogger.warn('createEmergencyPrimaryList', 'Created emergency primary list');
  return emergencyList;
}
